import React, { useCallback, useMemo } from 'react';

import { Box, Checkbox, CircularProgress, FormControlLabel, Link, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import ResponsiveImage from '~/components/Common/Elements/ResponsiveImage';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { DATAGRID_COMPACT_EMPTY_OVERLAY_SX, DATAGRID_EXPAND_TO_ROWS_SX } from '../styles';

interface OfferItem {
  id: string;
  title: string;
  imageId?: string;
  propertyId?: string;
  distanceMeters?: number;
  offerType?: string;
}

interface Props<T extends OfferItem> {
  offers: Array<T>;
  selectedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
  isLoading?: boolean;
  savedOfferIds?: Array<string>;
  offerLabel: string;
  emptyMessage: string;
}

type OfferRow = OfferItem & {
  isSaved?: boolean;
};

function formatDistance(distanceMeters: number): string {
  if (distanceMeters < 1000) {
    return `${Math.round(distanceMeters)} m`;
  }

  return `${(distanceMeters / 1000).toFixed(1)} km`;
}

function getOfferHref({ id }: OfferItem): string {
  return `${window.configs.LUX_CUSTOMER_PORTAL}/offer/${id}`;
}

export default function TagAssignmentsOfferList<T extends OfferItem>({
  offers,
  selectedOfferIds,
  onSelectionChange,
  isLoading = false,
  savedOfferIds = [],
  offerLabel,
  emptyMessage,
}: Props<T>) {
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const selectedOfferIdsSet = useMemo(() => new Set(selectedOfferIds), [selectedOfferIds]);
  const savedOfferIdsSet = useMemo(() => new Set(savedOfferIds), [savedOfferIds]);
  const showHotelDetails = offers.some((offer) => offer.propertyId || offer.distanceMeters !== undefined);

  const allSelected = useMemo(() => {
    if (offers.length === 0) {
      return false;
    }
    const selectableOffers = offers.filter((offer) => !savedOfferIdsSet.has(offer.id));
    if (selectableOffers.length === 0) {
      return false;
    }
    return selectableOffers.every((offer) => selectedOfferIdsSet.has(offer.id));
  }, [offers, selectedOfferIdsSet, savedOfferIdsSet]);

  const someSelected = useMemo(() => {
    if (offers.length === 0) {
      return false;
    }
    const selectableOffers = offers.filter((offer) => !savedOfferIdsSet.has(offer.id));
    return selectableOffers.some((offer) => selectedOfferIdsSet.has(offer.id)) && !allSelected;
  }, [offers, selectedOfferIdsSet, savedOfferIdsSet, allSelected]);

  const handleToggleSelectAll = useCallback(() => {
    const currentOfferIdsSet = new Set(offers.map((offer) => offer.id));

    if (allSelected) {
      const remainingSelected = selectedOfferIds.filter((id) => {
        if (savedOfferIdsSet.has(id)) {
          return true;
        }
        if (!currentOfferIdsSet.has(id)) {
          return true;
        }
        return false;
      });
      onSelectionChange(remainingSelected);
    } else {
      const selectableIds = offers.filter((offer) => !savedOfferIdsSet.has(offer.id)).map((offer) => offer.id);
      const newSelected = [...new Set([...selectedOfferIds, ...selectableIds])];
      onSelectionChange(newSelected);
    }
  }, [allSelected, offers, selectedOfferIds, savedOfferIdsSet, onSelectionChange]);

  const handleToggleOffer = useCallback(
    (offerId: string) => {
      if (savedOfferIdsSet.has(offerId)) {
        return;
      }
      const newSelected = selectedOfferIdsSet.has(offerId)
        ? selectedOfferIds.filter((id) => id !== offerId)
        : [...selectedOfferIds, offerId];
      onSelectionChange(newSelected);
    },
    [selectedOfferIds, selectedOfferIdsSet, savedOfferIdsSet, onSelectionChange],
  );

  const rows: Array<OfferRow> = useMemo(() => {
    return offers.map((offer) => ({
      ...offer,
      isSaved: savedOfferIdsSet.has(offer.id),
    }));
  }, [offers, savedOfferIdsSet]);

  const columns: Array<GridColDef<OfferRow>> = useMemo(
    () => [
      {
        field: 'checkbox',
        headerName: '',
        width: 72,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const isSelected = selectedOfferIdsSet.has(params.row.id);
          const isSaved = params.row.isSaved;
          return (
            <Checkbox
              checked={isSelected}
              onChange={() => handleToggleOffer(params.row.id)}
              onClick={(e) => e.stopPropagation()}
              disabled={isSaved}
            />
          );
        },
      },
      {
        field: 'id',
        headerName: showHotelDetails ? 'Offer ID' : 'ID',
        width: 200,
        flex: 1,
        renderCell: (params) => (
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%" gap={0.25}>
            {showHotelDetails ? (
              <Link
                href={getOfferHref(params.row)}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                variant="body2"
                lineHeight={1.2}
                onClick={(event) => event.stopPropagation()}
              >
                {params.row.id}
              </Link>
            ) : (
              <Typography variant="body2" lineHeight={1.2}>
                {params.row.id}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'image',
        headerName: 'Image',
        width: 150,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.imageId) {
            return <ResponsiveImage imageId={params.row.imageId} />;
          }
          return (
            <Box display="flex" alignItems="center" justifyContent="center" width={100} height={75} bgcolor="grey.200">
              <Typography variant="caption" color="text.secondary">
                No Image
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 2,
        renderCell: (params) => {
          return (
            <Box>
              <Typography variant="body2">{params.value}</Typography>
              {params.row.isSaved && (
                <Typography variant="caption" color="text.secondary">
                  Already assigned
                </Typography>
              )}
            </Box>
          );
        },
      },
      ...(showHotelDetails
        ? [
            {
              field: 'distanceMeters',
              headerName: 'Distance',
              width: 120,
              valueFormatter: (value: number | undefined) => (value === undefined ? '—' : formatDistance(value)),
            } satisfies GridColDef<OfferRow>,
          ]
        : []),
    ],
    [selectedOfferIdsSet, handleToggleOffer, showHotelDetails],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (offers.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <FormControlLabel
          control={<Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleToggleSelectAll} />}
          label={
            <Typography variant="subtitle1" fontWeight="medium">
              Select All {offerLabel} ({offers.length} offers)
            </Typography>
          }
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={showHotelDetails ? 80 : undefined}
        localeText={{ noRowsLabel: 'None' }}
        disableRowSelectionOnClick
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        disableColumnMenu
        getRowClassName={(params) => (params.row.isSaved ? 'MuiDataGrid-row--disabled' : '')}
        sx={[
          DATAGRID_EXPAND_TO_ROWS_SX,
          DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
          {
            '& .MuiDataGrid-row--disabled': {
              opacity: 0.5,
              pointerEvents: 'none',
            },
          },
        ]}
      />
    </Box>
  );
}
